import React, { useContext, useEffect, useRef } from 'react';
import { Form, Input, Modal } from 'antd';

import ApiSitesContext from '@totem/components/administration/apiSites/ApiSitesContext';
import { ObjectReference } from '@totem/components/administration/apiSites/types';
import OrgHierarchySelector from '@totem/components/ui_components/widgets/hierarchySelector/orgHierarchySelector';

const EMPTY_ID = '000000000000000000000000';

const ApiSiteEditDialog = () => {
  const { editingSite, modalOpen, onModalClose, onSave } =
    useContext(ApiSitesContext);
  const [form] = Form.useForm();

  const orgRef = useRef<ObjectReference>({ id: '', name: '' });
  const regionRef = useRef<ObjectReference>({ id: '', name: '' });
  const buildingRef = useRef<ObjectReference>({ id: '', name: '' });

  useEffect(() => {
    if (modalOpen && editingSite) {
      form.setFieldsValue({ displayName: editingSite.displayName });

      orgRef.current = editingSite.organization ?? { id: '', name: '' };

      regionRef.current = editingSite.region ?? { id: '', name: '' };

      buildingRef.current = editingSite.building ?? { id: '', name: '' };
    } else if (!modalOpen) {
      form.resetFields();

      orgRef.current = { id: '', name: '' };

      regionRef.current = { id: '', name: '' };

      buildingRef.current = { id: '', name: '' };
    }
  }, [modalOpen, editingSite, form]);

  const handleHierarchyChange = (
    org: ObjectReference,
    region: ObjectReference,
    building: ObjectReference,
    _cs: ObjectReference,
  ) => {
    orgRef.current = org;

    regionRef.current = region;

    buildingRef.current = building;
  };

  const handleOk = () => {
    form.validateFields().then((values) => {
      onSave({
        id: editingSite?.id,
        type: editingSite?.type,
        displayName: values.displayName,
        organization: orgRef.current,
        region: regionRef.current,
        building: buildingRef.current,
      });
    });
  };

  return (
    <Modal
      title="Edit API Site"
      open={modalOpen}
      onOk={handleOk}
      onCancel={onModalClose}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Display Name"
          name="displayName"
          rules={[{ required: true, message: 'Display name is required' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item label="Organization / Building">
          <OrgHierarchySelector
            showOrganization
            showRegions={false}
            showBuildings
            showControlSystems={false}
            allowUnassignedOrganization
            allowUnassignedBuilding
            defaultBuildingId={
              editingSite?.building?.id && editingSite.building.id !== EMPTY_ID
                ? editingSite.building.id
                : undefined
            }
            onChange={handleHierarchyChange}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ApiSiteEditDialog;
